import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Button from '../shared/Button';
import ConfirmModal from '../shared/ConfirmModal';
import '../AddPaymentPorterWizard.css';

// THE PMS SYSTEMS MCP AS A SERVICE SUPPORTS AS SOURCES — superadmin, on Admin › Platform › MCP as a Service (V643).
// The list hconnect keeps, each one a system from the Marketplace table; Add offers the active marketplace systems not on
// it yet (the same choice cards as the product wizards' system step), Remove takes one off. Both are audited by the API.
// "Hotels" is how many hotels are set up on that system as their PMS (V648).
// A row opens the system's page (McpSourcePage): the pipeline templates built for it. A system with templates (V644) cannot be
// removed until they are deleted (the API refuses).

const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function McpSourceSystems() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try { setData(await mcpServiceService.getSources()); } catch (e) { setError(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { setData(await fn()); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const onList = useMemo(() => new Set((data ? data.sources : []).map(s => s.marketplace_id)), [data]);
  const offered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ? data.marketplace : []).filter(m => !onList.has(m.marketplace_id)
      && (!q || (m.name || '').toLowerCase().includes(q) || (m.company || '').toLowerCase().includes(q)));
  }, [data, onList, search]);

  const openAdd = () => { setSearch(''); setPicked(null); setAdding(true); };
  const add = () => { const id = picked; setAdding(false); act(() => mcpServiceService.addSource(id)); };
  const remove = () => { const s = removing; setRemoving(null); act(() => mcpServiceService.removeSource(s.marketplace_id)); };

  return (
    <div className="mcps-card">
      <div className="mcps-card-head">
        <h3>Supported PMS systems</h3>
        <Button variant="primary" disabled={!data || busy} onClick={openAdd}>+ Add</Button>
      </div>
      <p className="mcps-meta">The property systems MCP as a Service can serve a hotel from. Each is a system from the Marketplace.</p>
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {!data && !error && <p>Loading…</p>}

      {data && (data.sources.length ? (
        <table className="mcps-table">
          <thead><tr><th>System</th><th>Company</th><th>Pipelines</th><th>Hotels</th><th>Added</th><th></th></tr></thead>
          <tbody>{data.sources.map(s => (
            <tr key={s.marketplace_id} className="clickable-row" style={{ cursor: 'pointer' }} title={`Open ${s.name}'s pipelines`}
                onClick={() => navigate(`/admin/mcp-service/sources/${s.id}`)}>
              <td>{s.name}{s.active !== 'Y' && <span className="mcps-pill mcps-pill-pending" style={{ marginLeft: '0.5rem' }}>inactive in Marketplace</span>}</td>
              <td>{s.company || '—'}</td>
              <td>{s.pipelines}</td>
              <td>{s.hotels}</td>
              <td>{when(s.created_at)}{s.created_by ? ` · ${s.created_by}` : ''}</td>
              <td onClick={e => e.stopPropagation()}>
                <Button variant="secondary" disabled={busy || Number(s.pipelines) > 0 || Number(s.hotels) > 0} onClick={() => setRemoving(s)}
                        title={Number(s.pipelines) > 0 ? 'Delete its pipeline templates first'
                          : Number(s.hotels) > 0 ? 'Hotels are set up on this PMS — move them to another first' : undefined}>Remove</Button></td>
            </tr>))}</tbody>
        </table>
      ) : <p className="mcps-meta">No systems yet. Add the PMS systems the service will support.</p>)}

      {adding && (
        <ConfirmModal wide title="Add a PMS system" confirmLabel="Add" confirmDisabled={!picked}
                      onCancel={() => setAdding(false)} onConfirm={add}>
          <input type="text" className="ppw-input" placeholder="Search by system or company…" value={search}
                 onChange={e => setSearch(e.target.value)} autoFocus style={{ marginBottom: '0.75rem' }} />
          <div className="ppw-connector-list">
            {offered.length ? offered.map(m => (
              <button key={m.marketplace_id} type="button"
                      className={`ppw-connector-card${picked === m.marketplace_id ? ' selected' : ''}`}
                      onClick={() => setPicked(m.marketplace_id)}>
                <div className="ppw-connector-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="ppw-connector-info">
                  <div className="ppw-connector-name">{m.name}</div>
                  {m.company && <div className="ppw-connector-meta">{m.company}</div>}
                </div>
              </button>
            )) : (
              <div className="ppw-empty-state">
                <strong>Nothing to add</strong>
                {search ? 'No system matches your search.' : 'Every active Marketplace system is already on the list.'}
              </div>
            )}
          </div>
        </ConfirmModal>
      )}

      {removing && (
        <ConfirmModal danger title={`Remove ${removing.name}?`} confirmLabel="Remove" onCancel={() => setRemoving(null)} onConfirm={remove}
                      message={`${removing.name} will no longer be listed as a supported source.`} />
      )}
    </div>
  );
}
